const USER_KEY = 'paysafego_platform_user'

function buildTimeApiOrigin() {
  try {
    const o = import.meta.env?.VITE_API_ORIGIN
    return o ? String(o).trim().replace(/\/$/, '') : ''
  } catch {
    return ''
  }
}

/**
 * Platform console is served from its own host (e.g. paysafego-platform.*) with nginx proxying /api → admin API.
 * If the build accidentally bakes the customer SPA origin (paysafego.appstaging.biz), login would hit the wrong
 * backend and return "Tenant required". Prefer same-origin /api on known platform hosts.
 */
function shouldUseSameOriginApi(bakedBase) {
  if (typeof window === 'undefined' || !bakedBase) return false
  try {
    const bakedUrl = bakedBase.includes('://') ? bakedBase : `https://${bakedBase}`
    const bakedOrigin = new URL(bakedUrl).origin
    if (window.location.origin === bakedOrigin) return false
    const h = window.location.hostname.toLowerCase()
    const bakedHost = new URL(bakedUrl).hostname.toLowerCase()
    // Explicit opt-in (set in platform console builds if needed)
    try {
      if (String(import.meta.env?.VITE_PLATFORM_SAME_ORIGIN_API || '').toLowerCase() === 'true') return true
    } catch {
      /* ignore */
    }
    // Known platform-console host patterns → nginx should proxy /api to platform admin API
    if (h.includes('paysafego-platform.')) return true
    if (h.includes('platform.paysafego.net')) return true // e.g. psgdevplatform.paysafego.net
    if (/^admin\./.test(h)) return true
    if (/-platform\./.test(h)) return true
    if (h.startsWith('platform.')) return true
    // Baked VITE_API_ORIGIN points at customer gateway but this tab is on another host (e.g. platform UI)
    if (bakedHost !== h && /(^|\.)paysafego\.appstaging\.biz$/i.test(bakedHost)) {
      const platformish =
        h.includes('paysafego-platform') ||
        h.includes('platform.paysafego.net') ||
        /^admin\./.test(h) ||
        /-platform\./.test(h) ||
        h.startsWith('platform.')
      if (platformish) return true
    }
  } catch {
    /* ignore */
  }
  return false
}

export function getLocalApiUrl(endpoint = '') {
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  const base = buildTimeApiOrigin()
  if (!base) return path
  if (shouldUseSameOriginApi(base)) return path
  return `${base}${path}`
}

function getAuthToken() {
  if (typeof window === 'undefined') return ''
  try {
    const userStr = localStorage.getItem(USER_KEY)
    return userStr ? JSON.parse(userStr).token || '' : ''
  } catch {
    return ''
  }
}

function getUrlPathname(url) {
  if (!url || typeof url !== 'string') return ''
  const noQuery = url.split('?')[0].split('#')[0]
  if (noQuery.startsWith('http://') || noQuery.startsWith('https://')) {
    try {
      return new URL(noQuery).pathname || ''
    } catch {
      return ''
    }
  }
  return noQuery
}

function isLoginRequestUrl(url) {
  const p = getUrlPathname(url)
  return p === '/api/login' || p.endsWith('/api/login')
}

export const authFetch = async (input, init = {}) => {
  const url = typeof input === 'string' ? input : input?.url || ''
  const headers = new Headers(init.headers || {})
  if (!isLoginRequestUrl(url)) {
    const token = getAuthToken()
    if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`)
  } else {
    headers.delete('Authorization')
  }
  const res = await fetch(input, { ...init, headers })
  if (res.status === 401 && !isLoginRequestUrl(url)) {
    localStorage.removeItem(USER_KEY)
    window.location.href = '/login?expired=1'
  }
  return res
}

export { USER_KEY }
